/**
 * @file svelte 组件渲染入口
 *
 * 所有的 svelte 组件都会用 svelte 高阶组件(wrapper-component)去包一层，以便统一处理 svelte 组件的共同逻辑。
 * 活动运行时对 svelte 组件提供的能力，是通过 actCompContext 来提供的，因此：
 * 本文件作为所有渲染 svelte 组件的入口，包含 svelte 高阶组件和 actCompContext 交互逻辑，
 * 这样 svelte 高阶组件中就不引入 actCompContext，从而降低 svelte 高阶组件的复杂性。如下图:
 *
 * ActRuntime <--> ActCompContext <--> render <--> WrapperComponent <--> SvelteComponent
 *
 */

import type { IComponentPropsConfig } from '@/svelte-comp-adapter/interface';
import { SvelteComponent } from 'svelte';

import WrapperComponent, { events as wrapperComponentEvents } from '../wrapper-component/index.svelte';
import { getActCompContext, getActCompContextInterface } from '../context/act-comp-context';

/**
 * 渲染 svelte 组件
 *
 * @param params 渲染参数
 * @param params.target 组件挂载父节点
 * @param params.props 组件属性
 */
export const renderSvelteComponent = (params: {
  /**
   * 组件属性
  */
  props: {
    /**
     * 组件渲染的节点
     */
    target: HTMLElement;
    /**
     * 组件配置
    */
    config: IComponentPropsConfig;
    /**
     * 组件选项
     */
    componentOptions: {
      attrs: Record<string, any>;
      style: Record<string, any>;
      class: string;
    };
    /**
     * 其他属性
     */
    [key: string]: any;
  };
}) => {
  const { props } = params;

  const config = props.config as Record<string, any>;

  // 这部分属于和 svelte 组件约定的逻辑
  // 根据约定，svelte组件属性的配置中可能包含 modId 、backendActId 、backendCompIid 三个字段
  // 当svelte组件在活动中使用，有 modId 时，需要自动为组件的配置填充 `backendActId` 和 backendCompIid 字段
  if (config.modId && window.mappingInfo) {
    config.backendActId = window.mappingInfo.activity_iid || '';
    config.backendCompIid = window.mappingInfo.compIdMapping[config.modId]?.component_iid || '';
  }

  // 这部分属于和 svelte 组件约定的逻辑
  // 有些组件不止有一个 modId，此时约定后缀 `ModId` 和 `CompIid` 作为关联的标志
  // 例如: aaBbModId => aaBbCompIid
  const configKeys = Object.keys(config);
  configKeys.filter(key => /ModId$/.test(key)).forEach((key) => {
    const prefix = key.replace('ModId', '');
    const compIidKey = `${prefix}CompIid`;
    // 仅当没有同名属性时才赋值，防止原有属性被覆盖
    if (!configKeys.includes(compIidKey)) {
      config[compIidKey] = window.mappingInfo.compIdMapping[config[key]]?.component_iid || '';
    }
  });

  // 每一次获取的 context 都是全新的
  const context = getActCompContext();

  // emit 增加来源组件(fromCpt)
  // 详见 packages/page/ui/packages/app/src/app.vue#bindEvents
  const emitFn = getActCompContextInterface().emit;
  context.set('emit', (name: string, ...args: any[]) => {
    emitFn(name, { config: { id: props?.config?.id } }, ...args);
  });

  // 实例化 svelte 高阶组件
  const wrapperComponent = new WrapperComponent({
    // 包装组件是无 ui 的，这里直接将包装组件渲染到 body 也不影响
    target: document.body,
    props,
    context: getActCompContext(),
  });

  // 真正的svelte组件
  let componentInstance: SvelteComponent<any, any, any>;

  // svelte 组件创建后，进行一系列额外的逻辑处理
  wrapperComponent.$on(wrapperComponentEvents.componentCreated, () => {
    const contextInterface = getActCompContextInterface();
    const baseType = wrapperComponent.getComponentBaseType();
    componentInstance = wrapperComponent.getComponentInstance();

    componentInstance.$options = {};

    // 管理台配置的组件事件
    if (componentInstance.magicTriggerEvents) {
      componentInstance.$options.magicTriggerEvents = componentInstance.magicTriggerEvents;
    }

    // 管理台配置的组件方法
    if (componentInstance.magicCallerMethods) {
      componentInstance.$options.magicCallerMethods = componentInstance.magicCallerMethods;
    }

    const app = contextInterface.actVm;
    switch (baseType) {
      case 'container': app.addContainer(componentInstance); break;
      case 'pop': app.addPop(componentInstance); break;
      default: app.addComponent(componentInstance); break;
    }
  });

  return {
    getComponentInstance: () => componentInstance,
    getComponentElement: () => wrapperComponent.getComponentElement(),
    updateComponentStyle: (style: Record<string, any>) => {
      wrapperComponent.updateComponentStyle(style);
    },
  };
};
